import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

/** The base32 secret inside an otpauth:// URL, for manual entry in the authenticator app. */
export function secretFromOtpauthUrl(url?: string): string | null {
  const match = url ? /[?&]secret=([A-Z2-7]+)/i.exec(url) : null;
  return match ? match[1] : null;
}

interface MfaSetupProps {
  qrCodeUrl?: string;
  handleVerifyMfaSetup: (code: string) => Promise<void>;
  error: string | null;
  isLoading: boolean;
  setView?: (view: string) => void;
  /**
   * Shows "Skip for now" (back to sign-in). Off by default: an MFA_SETUP challenge cannot be
   * skipped (signing in again asks for setup again) and the ACC-05 gate is mandatory.
   */
  allowSkip?: boolean;
}

const MfaSetup: React.FC<MfaSetupProps> = ({
  qrCodeUrl,
  handleVerifyMfaSetup,
  error,
  isLoading,
  setView,
  allowSkip = false
}) => {
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  
  const handleVerify = async () => {
    if (!code.trim()) {
      setLocalError('Please enter the verification code');
      return;
    }
    
    if (!/^\d{6}$/.test(code.trim())) {
      setLocalError('Please enter a valid 6-digit code');
      return;
    }
    
    setLocalError(null);
    await handleVerifyMfaSetup(code.trim());
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoading) {
      handleVerify();
    }
  };
  
  const handleSkip = () => {
    if (setView) {
      // Navigate to sign in screen (no credentials are kept in browser storage)
      setView('signin');
    }
  };
  
  return (
    <div className="w-full max-w-md py-8 md:py-10">
      <h2 className="text-2xl font-semibold mb-1 text-slate-800">Set up Two-Factor Authentication</h2>
      <p className="text-gray-500 mb-6 text-sm">Scan the QR code with an authenticator app and enter the verification code to secure your account.</p>
      
      {(error || localError) && (
        <div className="p-3 bg-red-500/10 border-l-4 border-red-500 text-red-500 rounded mb-6 text-sm">
          {error || localError}
        </div>
      )}
      
      <div className="flex flex-col gap-5 items-center">
        <div className="bg-white p-4 rounded-md border border-slate-200 mb-2">
          {qrCodeUrl ? (
            <QRCodeSVG value={qrCodeUrl} size={200} />
          ) : (
            <div className="flex items-center justify-center h-[200px] w-[200px] bg-gray-100">
              <p className="text-gray-400 text-sm text-center">Loading QR code...</p>
            </div>
          )}
        </div>
        
        <p className="text-sm text-center text-gray-600">
          Scan the QR code above with Google Authenticator, Authy, or another compatible authenticator app.
        </p>
        {secretFromOtpauthUrl(qrCodeUrl) && (
          <div className="w-full mb-4 text-center text-xs text-gray-600">
            <p>Can't scan it? Enter this setup key in the app:</p>
            <code className="mt-1 block break-all select-all rounded bg-slate-100 p-2 font-mono text-sm text-slate-800" data-testid="mfa-secret">
              {secretFromOtpauthUrl(qrCodeUrl)?.replace(/(.{4})/g, '$1 ').trim()}
            </code>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-2">
          <div className="flex flex-col gap-2 w-full">
            <label className="font-medium text-slate-800 text-sm" htmlFor="code">Verification Code</label>
            <input
              className="w-full p-3 border border-slate-200 rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
              type="text"
              id="code"
              name="code"
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </div>
          
          <button 
            type="submit"
            className={`w-full py-3 px-4 bg-slate-900 text-white rounded-md text-sm font-medium transition ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-slate-800'}`}
            disabled={isLoading}
          >
            {isLoading ? 'Verifying...' : 'Verify & Complete Setup'}
          </button>
        </form>
      </div>
      
      <div className="mt-6 text-center text-xs text-gray-500">
        <p>Two-factor authentication adds an extra layer of security to your account</p>
        {allowSkip && (
          <p className="mt-2">Having trouble? <button 
            onClick={handleSkip} 
            className="text-blue-600 hover:underline"
            type="button"
          >
            Skip for now
          </button></p>
        )}
      </div>
    </div>
  );
};

export default MfaSetup;
